import { Box, Container, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { Alert } from '@notablehealth/design-system';
import { Query, api, isDefined, parseError, useAuthContext } from 'common';

import backgroundImage from '../assets/legacy/login-background.svg';
import { Snow } from '../components/Snow';
import { setLoginEmail, setLoginRedirect } from '../utils/login';
import { LoginForm } from './Login/Form';

import styles from './login.module.css';

export function Login() {
  const location = useLocation();
  const queryParams = Query.parse(location.search);

  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState<string | undefined>(
    isDefined(queryParams.samlError)
      ? 'There was an error logging in with your SSO provider'
      : undefined
  );
  const authContext = useAuthContext();

  useEffect(() => {
    if (isDefined(queryParams.redirectSuccess)) {
      setLoginRedirect(queryParams.redirectSuccess);
    }
  }, [queryParams.redirectSuccess]);

  const handleLogin = async (email: string, password: string) => {
    let response;
    try {
      setLoggingIn(true);
      response = await api.client.User.login(email, password);
      setLoginEmail(response.user.email);
    } catch (error) {
      setLoggingIn(false);

      setError(parseError(error, 'There was an error logging in'));
      return;
    }

    setLoggingIn(false);

    if (!response.user) {
      setError('There was an error logging in!');
    } else {
      authContext.refreshLoginCheck();
    }
  };

  const handleSso = (endpoint: string) => {
    window.location.href = endpoint;
  };

  const getUserDetails = async (email: string) => await api.client.User.getUserDetails(email);

  if (authContext.status === 'resolved' && authContext.user?.registered) {
    return <Redirect to="/login-success" />;
  }

  return (
    <Box
      sx={{
        backgroundImage: `url("${backgroundImage}")`,
        backgroundSize: 'cover',
        height: '100vh',
      }}
    >
      <Snow />
      <Container sx={{ height: '100%', display: 'flex' }}>
        <Stack width="100%" alignItems="center" justifyContent="center" gap={4}>
          {error && (
            <Box width="100%">
              <Alert className={styles.Error} variant="danger">
                {error}
              </Alert>
            </Box>
          )}
          <LoginForm
            loading={loggingIn}
            handleSso={handleSso}
            handleLogin={handleLogin}
            getUserDetails={getUserDetails}
          />
        </Stack>
      </Container>
    </Box>
  );
}
